import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  SafeAreaView,
  View,
  Text,
  ScrollView,
  StyleSheet,
  Dimensions,
  Image,
  Pressable,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../navigation/stackTypes';
import { UserSessionAttributes } from '../models/user-session-model';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { notificationService } from '../services/notification-service';
import { updateUser } from '../store/actions/user-actions';
import Header from '../components/Header';
import { Platform } from 'react-native';
import AppInput from '../components/app-input';
import { isIOS } from '../components/common/utils/platform.util';
import LoadingButton from '../components/LoadingButton';
import PlacesAutocomplete, { Prediction } from '../components/google-places-autocomplete';
import { GetGoogleGeocode, getAddressComponentsFromGeodata } from '../components/common/utils/google-api-utils';
import { getUserInfo, updateBillingAddressToServer } from '../services/user-service';
import { isSuccessful } from '../services/api-requests';
// import { userActions } from '../store/slices/user-slice';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useMenu } from '../navigation/MenuContext';

const { height } = Dimensions.get('window');

type Props = NativeStackScreenProps<RootStackParamList, 'AccountInfo'>;

const AccountInfo: React.FC<Props> = ({ navigation, route }) => {
  const { openMenu } = useMenu();
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accountInfo, setAccountInfo] = useState({
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    full_address: '',
    address: '',
    street_name: '',
    street_number: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
  });
  const [billingLoading, setBillingLoading] = useState<boolean>(true);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [isEditingBilling, setIsEditingBilling] = useState<boolean>(false);
  const dispatch: AppDispatch = useDispatch();

  const session: UserSessionAttributes | undefined = useSelector(
    (state: RootState) => state.user.session
  );

  const isChangePassword = route?.params?.changePassword;

  const handleSubmit = async () => {
    if (session) {
      // Filter out empty strings
      const filteredAccountInfo = Object.fromEntries(
        Object.entries(accountInfo).filter(([_, value]) => value !== '')
      );

      // Only pass password if it's not an empty string
      const passwordToPass = password !== '' ? password : undefined;

      // Call updateUser only with defined and non-empty parameters
      const updated = await dispatch(
        updateUser(session, {
          first_name: filteredAccountInfo.first_name,
          last_name: filteredAccountInfo.last_name,
          email: filteredAccountInfo.email,
          password: passwordToPass,
        })
      );
      console.log('tried updating user and received', updated);
      // Save billing address as part of the same flow
      let billingUpdated = true;
      const { full_address, address, city, state, zip_code, country, street_name, street_number, address_line_2 } = accountInfo as any;
      const canSaveBilling = Boolean(full_address && city && state && zip_code && country);
      if (canSaveBilling) {
        try {
          const resp = await updateBillingAddressToServer({
            full_address,
            // address, // Removed - not in database schema
            address_line_2,
            street_name,
            street_number,
            city,
            state,
            zip_code,
            country,
          });
          billingUpdated = isSuccessful(resp.status);
          if (!billingUpdated) {
            notificationService.sendNotification('error', 'Failed to save address', (resp as any)?.payload?.message ?? '');
          }
        } catch (e) {
          billingUpdated = false;
          notificationService.sendNotification('error', 'Failed to save address');
        }
      }

      if (updated || billingUpdated) {
        notificationService.sendNotification('success', 'Your account was updated!');
      }
      if (!updated && !billingUpdated) {
        notificationService.sendNotification(
          'error',
          'Error: Your account was not updated'
        );
      }
      // Open the side menu instead of navigating to MyAccount screen
      openMenu();
    } else {
      notificationService.sendNotification(
        'error',
        'Invalid Session. Please log in again'
      );
    }
  };

  useEffect(() => {
    //we should not have an undefined session state here, throw the user out if they manage to get here without a valid session
    if (!session) {
      notificationService.sendNotification(
        'error',
        'You do not have a valid session'
      );
      navigation.navigate('WelcomeScreen');
    } else {
      setAccountInfo((prev) => ({
        ...prev,
        first_name: session.first_name,
        last_name: session.last_name,
        username: session.username,
        email: session.email,
      }));
      // Prefill billing address
      (async () => {
        try {
          const res = await getUserInfo();
          if (isSuccessful(res.status) && res.payload?.user) {
            const u: any = res.payload.user as any;
            setAccountInfo((prev) => ({
              ...prev,
              full_address: u.full_address || u.address || '',
              address: u.full_address ? u.full_address.split(',')[0] : (u.address || ''),
              street_name: u.street_name || '',
              street_number: u.street_number || '',
              city: u.city || '',
              state: u.state || '',
              zip_code: u.zip_code || '',
              country: u.country || '',
            }));
          }
        } catch (e) {
          // noop
        } finally {
          setBillingLoading(false);
        }
      })();
    }
  }, [session]);

  const onPlaceSelected = useCallback(async (place: Prediction) => {
    try {
      setBillingError(null);
      const geo = await GetGoogleGeocode(place.description);
      if (!geo) return;
      const comps = getAddressComponentsFromGeodata(geo);
      const addressLine = `${comps.street_number ?? ''}${comps.street_number ? ' ' : ''}${comps.street_name ?? ''}`.trim();

      setAccountInfo(prev => ({
        ...prev,
        full_address: place.description,
        address: addressLine,
        street_name: comps.street_name ?? '',
        street_number: comps.street_number ?? '',
        city: comps.city ?? '',
        state: comps.state ?? '',
        zip_code: comps.zip_code ?? '',
        country: comps.country ?? '',
      }));
    } catch (e) {
      setBillingError('Unable to resolve selected address.');
    }
  }, []);

  // canSaveBilling computed later for consolidated save

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={[styles.containerBgColor, { height: height - 65 }]}>
        {Platform.OS !== 'web' && (
          <Header
            headerText={
              isChangePassword ? 'Change Password' : 'Account Information'
            }
          />
        )}

        <ScrollView
          contentContainerStyle={styles.scrollViewContent}
          keyboardShouldPersistTaps="handled"
          automaticallyAdjustKeyboardInsets
        >
          <>
            <View className="flex items-center mt-6">
              <Image
                source={require('../../assets/meanderlogowhite_120x120.png')}
                style={{ width: 100, height: 100, alignItems: 'center' }}
              />
            </View>

            {/* Change password */}
            {isChangePassword ? (
              <View className={isIOS ? 'mt-3' : 'mt-12'}>
                <AppInput
                  label="Create New Password"
                  type="password"
                  value={password}
                  placeholder="New Password"
                  onChange={(e) => setPassword(e.nativeEvent.text)}
                  error={errors.password}
                />

                <View className="mt-3">
                  <AppInput
                    label="Confirm New Password"
                    type="password"
                    value={confirmPassword}
                    placeholder="Confirm New Password"
                    onChange={(e) => setConfirmPassword(e.nativeEvent.text)}
                    error={errors.password}
                  />
                </View>
              </View>
            ) : (
              <View>
                {/* First name */}
                <View className="mt-3">
                  <AppInput
                    label="First Name"
                    value={accountInfo?.first_name}
                    placeholder="Enter your first name"
                    onChange={(e) =>
                      setAccountInfo({
                        ...accountInfo,
                        first_name: e.nativeEvent.text,
                      })
                    }
                  />
                </View>

                {/* Last name */}
                <View className="mt-3">
                  <AppInput
                    label="Last Name"
                    value={accountInfo?.last_name}
                    placeholder="Enter your last name"
                    onChange={(e) =>
                      setAccountInfo({
                        ...accountInfo,
                        last_name: e.nativeEvent.text,
                      })
                    }
                  />
                </View>

                {/*Email */}
                <View className="mt-3">
                  <AppInput
                    label="Email"
                    value={accountInfo?.email}
                    placeholder="Enter your email"
                    onChange={(e) =>
                      setAccountInfo({
                        ...accountInfo,
                        email: e.nativeEvent.text,
                      })
                    }
                  />
                </View>

                {/* Username */}
                <View className="mt-3">
                  <AppInput
                    label="Username"
                    value={accountInfo?.username}
                    placeholder="Enter your username"
                    onChange={(e) =>
                      setAccountInfo({
                        ...accountInfo,
                        username: e.nativeEvent.text,
                      })
                    }
                    editable={session?.service === 'meander' ? true : false}
                  />

                  {session?.service !== 'meander' && (
                    <Text
                      className="p-3 w-full italic bg-gray-700"
                      style={{ color: 'red' }}
                    >
                      {`[${session?.service} login cannot be modified]`}
                    </Text>
                  )}
                </View>

                {/* Billing Address */}
                <View style={styles.billingContainer}>
                  {billingLoading ? (
                    <View style={styles.centerRow}><ActivityIndicator color="#39BEB7" /></View>
                  ) : (
                    <>
                      <PlacesAutocomplete
                        headerText="Billing Address"
                        headerStyle={styles.billingHeader}
                        value={accountInfo.full_address}
                        onPlaceSelected={onPlaceSelected}
                        error={billingError || undefined}
                      />

                      {accountInfo.full_address === '' ? (
                        <Text style={styles.billingHint}>Please input a billing address</Text>
                      ) : (
                        <View style={styles.card}>
                          <View style={styles.editToggleRow}>
                            <Pressable
                              onPress={() => setIsEditingBilling((prev) => !prev)}
                              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                              style={({ pressed }) => [{ opacity: pressed ? 0.7 : 1, flexDirection: 'row', alignItems: 'center' }]}
                            >
                              <MaterialCommunityIcons name={isEditingBilling ? 'pencil-off' : 'pencil'} size={18} color="#FFFFFF" />
                              <Text style={styles.editText}>{isEditingBilling ? 'Done' : 'Edit'}</Text>
                            </Pressable>
                          </View>
                          <View style={styles.row}> 
                            <Text style={styles.rowLabel}>Address</Text>
                            <Text style={styles.rowValue}>{accountInfo.address || '-'}</Text>
                          </View>
                          <View style={styles.row}>
                            <Text style={styles.rowLabel}>City</Text>
                            {isEditingBilling ? (
                              <TextInput
                                style={styles.input}
                                placeholder="City"
                                placeholderTextColor="#8D8E90"
                                value={accountInfo.city}
                                onChangeText={(text) => setAccountInfo(prev => ({ ...prev, city: text }))}
                              />
                            ) : (
                              <Text style={styles.rowValue}>{accountInfo.city || '-'}</Text>
                            )}
                          </View>
                          <View style={styles.row}>
                            <Text style={styles.rowLabel}>State</Text>
                            {isEditingBilling ? (
                              <TextInput
                                style={styles.input}
                                placeholder="State"
                                placeholderTextColor="#8D8E90"
                                value={accountInfo.state}
                                onChangeText={(text) => setAccountInfo(prev => ({ ...prev, state: text }))}
                                autoCapitalize="characters"
                                maxLength={30}
                              />
                            ) : (
                              <Text style={styles.rowValue}>{accountInfo.state || '-'}</Text>
                            )}
                          </View>
                          <View style={styles.row}>
                            <Text style={styles.rowLabel}>ZIP</Text>
                            {isEditingBilling ? (
                              <TextInput
                                style={styles.input}
                                placeholder="ZIP Code"
                                placeholderTextColor="#8D8E90"
                                value={accountInfo.zip_code}
                                onChangeText={(text) => setAccountInfo(prev => ({ ...prev, zip_code: text }))}
                                keyboardType="numeric"
                                maxLength={10}
                              />
                            ) : (
                              <Text style={styles.rowValue}>{accountInfo.zip_code || '-'}</Text>
                            )}
                          </View>
                          <View style={styles.row}>
                            <Text style={styles.rowLabel}>Country</Text>
                            <Text style={styles.rowValue}>{accountInfo.country || '-'}</Text>
                          </View>
                        </View>
                      )}
                    </>
                  )}
                </View>
              </View>
            )}
          </>
        </ScrollView>

        {isChangePassword ? (
          <View style={styles.buttonContainer}>
            <LoadingButton
              onPress={handleSubmit}
              backgroundColor={styles.button.backgroundColor}
            >
              Save Password
            </LoadingButton>
          </View>
        ) : (
          <View style={styles.buttonContainer}>
            <LoadingButton
              onPress={handleSubmit}
              backgroundColor={styles.button.backgroundColor}
            >
              Save Account Information
            </LoadingButton>
          </View>
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#1A1D21',
  },
  containerBgColor: {
    flex: 1,
    backgroundColor: '#1A1D21',
    paddingBottom: 60,
  },
  scrollViewContent: {
    flexGrow: 1,
    padding: 16,
  },
  BgColor1: {
    backgroundColor: '#292D33',
  },
  BgColorDisabled: {
    backgroundColor: '#3A3B3C', // Dark gray background for disabled look
    color: '#B0B0B0', // Lighter gray text color
    opacity: 0.5, // Faded effect
  },
  buttonContainer: {
    position: 'absolute',
    bottom: 20,
    left: 16,
    right: 16,
  },
  button: {
    backgroundColor: '#39BEB7',
    padding: 16,
    borderRadius: 9999,
    alignItems: 'center',
  },
  mainHead: {
    color: 'white',
    fontSize: 18,
    alignSelf: 'center',
    fontWeight: '400',
  },
  billingContainer: {
    marginTop: 16,
  },
  billingHeader: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  billingHint: {
    color: '#39BEB7',
    marginTop: 8,
  },
  card: {
    backgroundColor: '#203D3C',
    borderRadius: 8,
    padding: 16,
    marginTop: 8,
  },
  editToggleRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 8,
  },
  editText: {
    color: '#FFFFFF',
    marginLeft: 6,
    fontSize: 12,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  rowLabel: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  rowValue: {
    color: '#FFFFFF',
    marginLeft: 12,
    flexShrink: 1,
    textAlign: 'right',
  },
  input: {
    color: '#FFFFFF',
    backgroundColor: '#0B1E1D',
    borderWidth: 1,
    borderColor: '#438b88',
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 10,
    minWidth: 160,
    textAlign: 'right',
  },
  addrSaveButton: {
    backgroundColor: '#39BEB7',
    marginTop: 16,
    paddingVertical: 12,
    borderRadius: 45,
    alignItems: 'center',
  },
  saveText: {
    color: '#fff',
    fontWeight: '600',
  },
  centerRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 8,
  },
});

export default AccountInfo;
